/**
 * ABCAssetViewer Component - Display ABC notation assets with player controls
 * Based on midi2abc.html template but without upload/status/instructions sections
 * 
 * Author: Markus van Kempen - markus.van.kempen@gmail.com
 * Last Updated: November 2025
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from './ToastContainer';
import { useAuth } from '../context/AuthContext';
import AssetEditModal from './AssetEditModal';
import ShareModal from './ShareModal';
import UserContentFeedback from './UserContentFeedback';
import LoadingSpinner from './LoadingSpinner';
import { decodeHtmlEntities } from '../utils/htmlDecode';
import { triggerShare } from '../utils/shareContent';
import TuneFormatMarker from './TuneFormatMarker';
import SEO from './SEO';
import './ABCAssetViewer.css';

/** API may return HTML-encoded titles; decode for display. React text nodes are already XSS-safe without manual escaping. */
const displayRelatedText = (v) => {
  if (v == null || v === '') return '';
  return decodeHtmlEntities(String(v));
};

const ABCAssetViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const { isAuthenticated, currentUser } = useAuth();
  const [asset, setAsset] = useState(null);
  const [abcContent, setAbcContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentBPM, setCurrentBPM] = useState(120);
  const [currentInstrument, setCurrentInstrument] = useState('piano-soundfont-fluidr3');
  const scoreDivRef = useRef(null);
  const abcTextareaRef = useRef(null);
  const [currentVisualObj, setCurrentVisualObj] = useState(null);
  const [audioContext, setAudioContext] = useState(null);
  const [piano, setPiano] = useState(null);
  const [midiPlayer, setMidiPlayer] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeAudioNodes, setActiveAudioNodes] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(true); // Start as true to show loading on page load
  const [initialLoadDelay, setInitialLoadDelay] = useState(true); // Disable for 5 seconds on initial load
  const [useSimpleSynth, setUseSimpleSynth] = useState(false);
  const [librariesReady, setLibrariesReady] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedTunes, setRelatedTunes] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(true);
  const [midiConversionReady, setMidiConversionReady] = useState(false);
  const [convertingMidi, setConvertingMidi] = useState(false);
  const initializingAudioRef = useRef(false);
  const audioContextRef = useRef(null);
  const isPlayingRef = useRef(false);
  const midiPlayerRef = useRef(null);
  const isPlayingMidiRef = useRef(false);

  // Load ABC asset
  useEffect(() => {
    loadAsset();

    // Disable play button for 10 seconds on initial page load
    setInitialLoadDelay(true);
    const delayTimer = setTimeout(() => {
      setInitialLoadDelay(false);
    }, 10000); // 10 seconds

    return () => {
      clearTimeout(delayTimer);
    };
    // loadAsset is intentionally keyed only by asset id (full deps would recreate unstable callbacks)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Load related products and tunes when asset is loaded
  useEffect(() => {
    if (asset && asset.id) {
      loadRelatedContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset]);

  // Load required scripts
  useEffect(() => {
    const loadScripts = async () => {
      try {
        // Initialize global variables required by ABCJS (must be done before loading ABCJS)
        if (typeof window.gShowABCJSRenderProgress === 'undefined') {
          window.gShowABCJSRenderProgress = false;
          window.gLastWarp = 100;
          window.gLastPlayerRepeat = false;
          window.gLocalStorageAvailable = false;
          try {
            if (typeof (Storage) !== "undefined" && window.localStorage) {
              const test = '__localStorage_test__';
              localStorage.setItem(test, test);
              localStorage.removeItem(test);
              window.gLocalStorageAvailable = true;
            }
          } catch (e) {
            window.gLocalStorageAvailable = false;
          }
          window.gDefaultSoundFont = "https://paulrosen.github.io/midi-js-soundfonts/FluidR3_GM/";
          window.gTheActiveSoundFont = window.gDefaultSoundFont;
          window.gIsIOS = false;
          window.gIsAndroid = false;
          window.gIsSafari = false;
          window.gIsChrome = false;

          // Browser detection
          if (typeof navigator !== 'undefined') {
            const ua = navigator.userAgent || navigator.vendor || window.opera;
            window.gIsIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
            window.gIsAndroid = /android/i.test(ua);
            window.gIsSafari = /^((?!chrome|android).)*safari/i.test(ua);
            window.gIsChrome = /chrome/i.test(ua) && !/edge/i.test(ua);
          }
        }

        // Load ABCJS from local files (served by backend)
        if (typeof window.ABCJS === 'undefined') {
          const abcjsScript = document.createElement('script');
          abcjsScript.src = '/tools/abctools/abcjs-basic-eskin-min.js';
          abcjsScript.async = true;
          document.body.appendChild(abcjsScript);

          await new Promise((resolve, reject) => {
            abcjsScript.onload = resolve;
            abcjsScript.onerror = () => {
              console.error('Failed to load ABCJS from local path, trying CDN...');
              // Try CDN fallback
              const cdnScript = document.createElement('script');
              cdnScript.src = 'https://cdn.jsdelivr.net/npm/abcjs@6.1.6/dist/abcjs-basic.min.js';
              cdnScript.async = true;
              document.body.appendChild(cdnScript);
              cdnScript.onload = resolve;
              cdnScript.onerror = () => reject(new Error('Failed to load ABCJS'));
            };
          });
        }

        // Load Soundfont Player from local files (served by backend)
        if (typeof window.Soundfont === 'undefined') {
          const soundfontScript = document.createElement('script');
          soundfontScript.src = '/tools/abctools/soundfont-player.min.js';
          soundfontScript.async = true;
          document.body.appendChild(soundfontScript);

          await new Promise((resolve, reject) => {
            soundfontScript.onload = resolve;
            soundfontScript.onerror = () => {
              console.error('Failed to load Soundfont Player from local path');
              reject(new Error('Failed to load Soundfont Player'));
            };
          });
        }

        setLibrariesReady(true);
      } catch (error) {
        console.error('Error loading scripts:', error);
        showError('Failed to load required libraries. Some features may not work.');
        // Still set ready to allow basic functionality
        setLibrariesReady(true);
      }
    };

    loadScripts();
  }, [showError]);

  // Initialize libraries
  useEffect(() => {
    if (abcContent && librariesReady) {
      initializeLibraries();
    }
  }, [abcContent, librariesReady]);

  // Pre-initialize audio context when libraries are ready
  useEffect(() => {
    if (librariesReady && !audioContextRef.current && !initializingAudioRef.current) {
      const preInitAudio = async () => {
        try {
          setIsLoadingAudio(true);
          await initAudioContext(true); // Wait for soundfont to load
          setIsLoadingAudio(false);
        } catch (err) {
          console.error('Error pre-initializing audio:', err);
          // Don't show error to user, just fall back to simple synth
          setIsLoadingAudio(false);
        }
      };
      preInitAudio();
    } else if (!librariesReady) {
      // Keep loading state while libraries are loading
      setIsLoadingAudio(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [librariesReady]);

  // Render notation when ABC content changes
  useEffect(() => {
    if (abcContent && librariesReady && scoreDivRef.current) {
      renderNotation(abcContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abcContent, librariesReady]);

  // Load MIDI conversion libraries
  const loadMidiConversionLibraries = async () => {
    if (midiConversionReady) return true;

    try {
      // Load Tone.js and Magenta.js core from CDN
      if (typeof window.Tone === 'undefined') {
        const toneScript = document.createElement('script');
        toneScript.src = 'https://cdn.jsdelivr.net/npm/tone@14.7.77/build/Tone.js';
        document.body.appendChild(toneScript);

        await new Promise((resolve, reject) => {
          toneScript.onload = resolve;
          toneScript.onerror = () => reject(new Error('Failed to load Tone.js'));
        });
      }

      // Load Magenta.js core
      let core;
      try {
        const magentaModule = await import(/* webpackIgnore: true */ 'https://cdn.jsdelivr.net/npm/@magenta/music@1.23.1/es6/core.js');
        core = magentaModule.default || magentaModule.core || magentaModule;

        // Also check window.core from script tag
        if (typeof window.core !== 'undefined') {
          core = window.core;
        }
        window.core = core; // Make available globally
      } catch (err) {
        console.error('Error loading Magenta core:', err);
        // Try script tag approach
        const coreScript = document.createElement('script');
        coreScript.src = 'https://cdn.jsdelivr.net/npm/@magenta/music@1.23.1/es6/core.js';
        document.body.appendChild(coreScript);

        await new Promise((resolve, reject) => {
          coreScript.onload = () => {
            if (typeof window.core !== 'undefined') {
              resolve();
            } else {
              // Wait a bit for it to initialize
              setTimeout(() => {
                if (typeof window.core !== 'undefined') {
                  resolve();
                } else {
                  reject(new Error('Core not available after loading'));
                }
              }, 1000);
            }
          };
          coreScript.onerror = () => reject(new Error('Failed to load Magenta core'));
        });
        core = window.core;
      }

      // Load midi2abc conversion function
      // Since it's a static file served by backend, we need to load it dynamically
      if (typeof window.tone2abc === 'function') {
        console.log('tone2abc already loaded');
        setMidiConversionReady(true);
        return true;
      }

      // Load midi2abc.js using script tag with module import (works with webpack)
      const loadMidi2abc = (path) => {
        return new Promise((resolve, reject) => {
          // Check if already loaded
          if (typeof window.tone2abc === 'function') {
            console.log('midi2abc already loaded');
            resolve(true);
            return;
          }

          // Create a loader script that will import the module
          // Load script via src attribute instead of inline to avoid webpack analysis
          const loaderScript = document.createElement('script');
          loaderScript.type = 'text/javascript';
          // Create a blob URL with the loader code to completely bypass webpack
          const loaderCode = `
            (async () => {
              try {
                // Use setTimeout to defer execution and avoid webpack analysis
                const loadModule = new Function('modulePath', 
                  'return (async () => { ' +
                  'const mod = await imp' + 'ort(modulePath); ' +
                  'window.tone2abc = mod.default || mod.tone2abc; ' +
                  'window.__midi2abcReady = true; ' +
                  'console.log("midi2abc loaded from", modulePath); ' +
                  '})();'
                );
                await loadModule('${path}');
              } catch (err) {
                console.error('Error loading midi2abc:', err);
                window.__midi2abcError = err.message;
              }
            })();
          `;
          const blob = new Blob([loaderCode], { type: 'text/javascript' });
          const blobUrl = URL.createObjectURL(blob);
          loaderScript.src = blobUrl;
          loaderScript.onload = () => URL.revokeObjectURL(blobUrl);

          loaderScript.onerror = (err) => {
            console.error(`Error loading script from ${path}:`, err);
            reject(new Error(`Failed to load ${path}`));
          };

          // Check for ready state
          const checkReady = () => {
            if (typeof window.tone2abc === 'function') {
              console.log('midi2abc loaded successfully');
              resolve(true);
            } else if (window.__midi2abcError) {
              reject(new Error(window.__midi2abcError));
            } else {
              setTimeout(checkReady, 100);
            }
          };

          // Start checking after a short delay
          setTimeout(checkReady, 100);

          // Set a timeout
          setTimeout(() => {
            if (typeof window.tone2abc !== 'function') {
              reject(new Error('Timeout waiting for midi2abc to load'));
            }
          }, 10000);

          document.head.appendChild(loaderScript);
        });
      };

      // Try primary path first
      try {
        await loadMidi2abc('/tools/abctools/midi2abc.js');
        console.log('Loaded midi2abc successfully from primary path');
        setMidiConversionReady(true);
        return true;
      } catch (err) {
        console.log('Primary path failed, trying alternative path...');
        // Try alternative path
        try {
          await loadMidi2abc('/tools/abctools/tools/midi2abc/midi2abc.js');
          console.log('Loaded midi2abc successfully from alternative path');
          setMidiConversionReady(true);
          return true;
        } catch (err2) {
          console.error('Both paths failed:', err2);
          throw new Error('Failed to load MIDI to ABC converter from any method');
        }
      }
    } catch (error) {
      console.error('Error loading MIDI conversion libraries:', error);
      return false;
    }
  };

  // Convert MIDI file to ABC
  const convertMIDIToABC = async (midiBlob, assetData) => {
    try {
      setConvertingMidi(true);

      // Ensure libraries are loaded
      const librariesReady = await loadMidiConversionLibraries();
      if (!librariesReady) {
        throw new Error('MIDI conversion libraries not available');
      }

      const core = window.core;
      const tone2abc = window.tone2abc;

      if (!core || !tone2abc) {
        throw new Error('Conversion libraries not loaded');
      }

      if (typeof core.blobToNoteSequence !== 'function') {
        throw new Error('blobToNoteSequence function not available');
      }

      console.log('Converting MIDI to ABC...');

      // Convert MIDI file to note sequence using Magenta.js
      const noteSequence = await core.blobToNoteSequence(midiBlob);

      // Extract metadata from asset description if available
      let metadata = {};
      if (assetData?.description) {
        const abcMatch = assetData.description.match(/\[ABC_METADATA\](.*?)\[\/ABC_METADATA\]/s);
        if (abcMatch) {
          try {
            metadata = JSON.parse(abcMatch[1]);
          } catch (e) {
            console.error('Error parsing ABC metadata:', e);
          }
        }
      }

      // Convert note sequence to ABC using tone2abc
      // tone2abc takes (noteSequence, options) where options can have title, composer, etc.
      const abcString = tone2abc(noteSequence, {
        title: metadata.tune_title || assetData?.original_filename?.replace(/\.(mid|midi)$/i, '') || 'Converted MIDI',
        composer: metadata.composer_arranger || undefined
      });

      if (!abcString || !abcString.trim()) {
        throw new Error('Conversion produced empty ABC string');
      }

      console.log('MIDI converted to ABC successfully');
      return abcString;
    } catch (error) {
      console.error('Error converting MIDI to ABC:', error);
      throw error;
    } finally {
      setConvertingMidi(false);
    }
  };

  const loadAsset = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/assets/${id}`);

      if (!response.ok) {
        throw new Error('Asset not found');
      }

      const assetData = await response.json();

      // Handle different response structures
      const asset = assetData.asset || assetData;

      // Debug: log the full response to understand structure
      if (!asset.id) {
        console.log('Full API response:', assetData);
        console.log('Asset keys:', Object.keys(asset));
      }

      console.log('Asset data:', {
        id: asset.id,
        filename: asset.original_filename || asset.filename,
        category: asset.category,
        mime_type: asset.mime_type,
        file_type: asset.file_type
      });

      setAsset(asset);

      // Check if it's a MIDI file
      const fileName = (asset.original_filename || asset.filename || '').toLowerCase();
      const isMidiFile = fileName.endsWith('.mid') ||
        fileName.endsWith('.midi') ||
        asset.mime_type === 'audio/midi' ||
        asset.mime_type === 'audio/mid' ||
        asset.mime_type === 'audio/x-midi' ||
        asset.file_type === 'music';

      if (isMidiFile) {
        // Download MIDI file as blob
        const fileResponse = await fetch(`/api/assets/${id}/download`);
        if (!fileResponse.ok) {
          throw new Error('Failed to download MIDI file');
        }
        const midiBlob = await fileResponse.blob();

        // Convert MIDI to ABC
        const abcString = await convertMIDIToABC(midiBlob, asset);
        setAbcContent(abcString);
        return;
      }

      // Otherwise, treat as ABC file
      const fileResponse = await fetch(`/api/assets/${id}/download`);
      if (!fileResponse.ok) {
        throw new Error('Failed to download asset file');
      }
      const fileText = await fileResponse.text();

      // Check if it's an ABC file by:
      // 1. File extension
      // 2. Category
      // 3. MIME type
      // 4. Content (looks like ABC notation)
      const hasABCExtension = fileName.endsWith('.abc') || fileName.endsWith('.txt');
      const isABCCategory = asset.category === 'ABC-Tune';
      const isTextMime = asset.mime_type === 'text/plain' ||
        asset.mime_type === 'text/x-abc' ||
        asset.mime_type === 'application/x-abc' ||
        asset.file_type === 'document';
      const looksLikeABC = fileText.trim() && (
        fileText.includes('X:') ||
        fileText.includes('T:') ||
        fileText.includes('K:') ||
        fileText.includes('M:') ||
        fileText.match(/^[A-Ga-g][#b]?[0-9]/) // ABC note pattern
      );

      const isABCFile = hasABCExtension || isABCCategory || isTextMime || looksLikeABC;

      if (!isABCFile) {
        console.error('Asset validation failed:', {
          hasABCExtension,
          isABCCategory,
          isTextMime,
          looksLikeABC,
          fileName,
          category: asset.category,
          mime_type: asset.mime_type,
          filePreview: fileText.substring(0, 100)
        });
        throw new Error('This asset does not appear to be an ABC tune file or MIDI file. Please ensure the file has a .abc, .txt, .mid, or .midi extension, or is categorized as ABC-Tune.');
      }

      // Basic validation - check if it looks like ABC notation
      if (!fileText.trim()) {
        throw new Error('File appears to be empty');
      }

      if (!looksLikeABC) {
        console.warn('File content may not be valid ABC notation, but proceeding anyway');
      }

      setAbcContent(fileText);

    } catch (error) {
      console.error('Error loading asset:', error);
      showError(`Failed to load asset: ${error.message}`);
      navigate('/tunes');
    } finally {
      setLoading(false);
    }
  };

  const initializeLibraries = async () => {
    // Libraries should already be loaded by the script loading effect
    if (typeof window.ABCJS === 'undefined' || typeof window.Soundfont === 'undefined') {
      console.error('Required libraries not loaded');
      return;
    }
  };

  const renderNotation = (abcString) => {
    if (!window.ABCJS || !scoreDivRef.current) return;

    try {
      scoreDivRef.current.innerHTML = '';

      const renderOptions = {
        responsive: "resize",
        add_classes: true,
        scale: 1.0,
        staffwidth: 750,
        paddingtop: 20,
        paddingbottom: 20,
        paddingleft: 20,
        paddingright: 20,
        color: '#000000',
        staffcolor: '#000000',
        textcolor: '#000000'
      };

      const visualObj = window.ABCJS.renderAbc(scoreDivRef.current, abcString, renderOptions);
      setCurrentVisualObj(visualObj);

      // Force black color for SVG elements
      setTimeout(() => {
        const svg = scoreDivRef.current?.querySelector('svg');
        if (svg) {
          svg.style.background = 'white';
          svg.style.opacity = '1';
          svg.style.visibility = 'visible';

          const paths = svg.querySelectorAll('path');
          paths.forEach(path => {
            path.setAttribute('stroke', 'black');
            path.setAttribute('fill', 'black');
            path.style.opacity = '1';
            path.style.visibility = 'visible';
          });

          const texts = svg.querySelectorAll('text');
          texts.forEach(text => {
            text.setAttribute('fill', 'black');
            text.style.opacity = '1';
            text.style.visibility = 'visible';
          });
        }
      }, 100);
    } catch (err) {
      console.error('Error rendering notation:', err);
      showError('Failed to render notation');
    }
  };

  const initAudioContext = async (waitForSoundfont = true) => {
    if (audioContextRef.current) {
      // If soundfont needs to be loaded and we don't have piano yet, load it
      if (waitForSoundfont && !useSimpleSynth && !piano) {
        await loadSoundfont(currentInstrument, audioContextRef.current);
      }
      return audioContextRef.current;
    }
    if (initializingAudioRef.current) {
      // Wait for initialization to complete
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (audioContextRef.current && !initializingAudioRef.current) {
            clearInterval(checkInterval);
            // If we need to wait for soundfont, ensure it's loaded
            if (waitForSoundfont && !useSimpleSynth && !piano) {
              loadSoundfont(currentInstrument, audioContextRef.current).then(() => {
                resolve(audioContextRef.current);
              });
            } else {
              resolve(audioContextRef.current);
            }
          }
        }, 50);
        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve(null);
        }, 5000);
      });
    }

    initializingAudioRef.current = true;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
      setAudioContext(ctx);
      if (waitForSoundfont) {
        await loadSoundfont(currentInstrument, ctx);
      }
      return ctx;
    } catch (err) {
      console.error('Error initializing audio:', err);
      audioContextRef.current = null;
      throw err;
    } finally {
      initializingAudioRef.current = false;
    }
  };

  const loadSoundfont = async (instrumentType, ctx = null) => {
    const audioCtx = ctx || audioContextRef.current || audioContext;
    if (!audioCtx) {
      const newCtx = await initAudioContext();
      if (!newCtx) {
        console.error('Failed to initialize audio context for soundfont');
        return;
      }
      return loadSoundfont(instrumentType, newCtx);
    }

    if (midiPlayer) {
      midiPlayer.stop();
    }
    stopAllNotes();

    const isMusyngKite = instrumentType.includes('musyngkite');
    const isSimple = instrumentType.includes('simple');

    setUseSimpleSynth(isSimple);
    setCurrentInstrument(instrumentType);

    if (isSimple) {
      setPiano(null);
      return;
    }

    try {
      const soundfontName = isMusyngKite ? 'MusyngKite' : 'FluidR3_GM';
      const instrumentName = 'acoustic_grand_piano';

      if (window.Soundfont) {
        const nameToUrl = (name, soundfont, format = 'mp3') => {
          const basePath = isMusyngKite
            ? '/tools/abctools/soundfonts/MusyngKite'
            : '/tools/abctools/soundfonts/FluidR3_GM';
          return `${basePath}/${name}-${format}.js`;
        };

        const instrument = await window.Soundfont.instrument(audioCtx, instrumentName, {
          nameToUrl: nameToUrl,
          soundfont: soundfontName,
          format: 'mp3'
        });
        setPiano(instrument);
      }
    } catch (err) {
      console.error('Error loading soundfont:', err);
      setUseSimpleSynth(true);
      setPiano(null);
    }
  };

  const handlePlay = async () => {
    if (!currentVisualObj || currentVisualObj.length === 0) {
      // Don't show error, just return silently
      return;
    }

    // Don't allow play if already playing - user must click stop first
    if (isPlaying) {
      return;
    }

    if (isLoadingAudio || loading) {
      // Already loading, wait a bit
      return;
    }

    setIsLoadingAudio(true);

    try {
      // Ensure audio context and soundfont are ready before playing
      let ctx = audioContextRef.current || audioContext;
      if (!ctx) {
        ctx = await initAudioContext(true); // Wait for soundfont to load
      } else {
        // If context exists but soundfont might not be loaded, ensure it's loaded
        if (!useSimpleSynth && !piano) {
          await loadSoundfont(currentInstrument, ctx);
        }
      }

      if (!ctx) {
        // Don't show error, just return silently
        setIsLoadingAudio(false);
        return;
      }

      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      // Wait a bit more to ensure soundfont is fully ready
      if (!useSimpleSynth && piano) {
        // Soundfont is loaded, give it a moment to be ready
        await new Promise(resolve => setTimeout(resolve, 50));
      } else if (!useSimpleSynth) {
        // Still loading soundfont, wait a bit longer
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      const midiData = window.ABCJS.synth.getMidiFile(currentVisualObj[0], {
        qpm: currentBPM,
        midiTempoAdjustment: 0
      });

      if (!midiData) {
        // Don't show error, just return silently
        setIsLoadingAudio(false);
        return;
      }

      let arrayBuffer;
      if (midiData instanceof Uint8Array) {
        arrayBuffer = midiData.buffer.slice(midiData.byteOffset, midiData.byteOffset + midiData.byteLength);
      } else if (midiData instanceof ArrayBuffer) {
        arrayBuffer = midiData;
      } else if (typeof midiData === 'string') {
        const dataUrlMatch = midiData.match(/href="(data:audio\/midi[^"]+)"/);
        let dataUrl = dataUrlMatch && dataUrlMatch[1];
        if (!dataUrl) {
          const altMatch = midiData.match(/data:audio\/midi,([^"]+)/);
          if (altMatch && altMatch[1]) {
            dataUrl = 'data:audio/midi,' + altMatch[1];
          }
        }
        if (dataUrl) {
          const response = await fetch(dataUrl);
          arrayBuffer = await response.arrayBuffer();
        }
      }

      if (arrayBuffer && arrayBuffer.byteLength > 0) {
        // Stop any existing playback before loading new data
        if (midiPlayer) {
          midiPlayer.stop();
        }
        stopAllNotes();

        await loadMidiData(arrayBuffer);

        // Get the current player from ref (most up-to-date)
        const currentPlayer = midiPlayerRef.current;
        if (currentPlayer) {
          // Set playing state before starting
          isPlayingRef.current = true;
          setIsPlaying(true);

          // Small delay to ensure state is set
          await new Promise(resolve => setTimeout(resolve, 10));

          // Only play if we're still supposed to be playing and player hasn't changed
          if (isPlayingRef.current && currentPlayer === midiPlayerRef.current) {
            currentPlayer.play();
          } else {
            // State changed, don't play
            isPlayingRef.current = false;
            setIsPlaying(false);
            isPlayingMidiRef.current = false;
          }
        } else {
          // Don't show error, just log it
          console.error('MIDI player not ready');
          isPlayingMidiRef.current = false;
        }
      } else {
        // Don't show error, just log it
        console.error('Could not extract MIDI data');
        isPlayingMidiRef.current = false;
      }
    } catch (err) {
      console.error('Error playing:', err);
      // Don't show error to user, just log it
      isPlayingMidiRef.current = false;
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const handleStop = () => {
    // Reset playing state FIRST to stop event processing
    isPlayingRef.current = false;
    setIsPlaying(false);
    isPlayingMidiRef.current = false;

    // Stop MIDI player from both state and ref
    const playerToStop = midiPlayer || midiPlayerRef.current;
    if (playerToStop) {
      try {
        // Try different stop methods
        if (typeof playerToStop.stop === 'function') {
          playerToStop.stop();
        }
        if (typeof playerToStop.pause === 'function') {
          playerToStop.pause();
        }
        if (typeof playerToStop.reset === 'function') {
          playerToStop.reset();
        }
        // Remove all event listeners
        if (typeof playerToStop.removeAllListeners === 'function') {
          playerToStop.removeAllListeners();
        }
        // If it's a MidiPlayer instance, try to stop all tracks
        if (playerToStop.tracks && Array.isArray(playerToStop.tracks)) {
          playerToStop.tracks.forEach(track => {
            if (track && typeof track.stop === 'function') {
              track.stop();
            }
          });
        }
      } catch (err) {
        console.error('Error stopping MIDI player:', err);
      }
    }

    // Stop all active audio nodes immediately
    stopAllNotes();

    // Clear both state and ref
    setMidiPlayer(null);
    midiPlayerRef.current = null;
  };

  const loadMidiData = async (arrayBuffer) => {
    // Stop and clean up any existing player completely
    const existingPlayer = midiPlayer || midiPlayerRef.current;
    if (existingPlayer) {
      try {
        // Set playing to false first
        isPlayingRef.current = false;
        setIsPlaying(false);
        isPlayingMidiRef.current = false;

        // Stop the player
        if (typeof existingPlayer.stop === 'function') {
          existingPlayer.stop();
        }
        if (typeof existingPlayer.pause === 'function') {
          existingPlayer.pause();
        }
        if (typeof existingPlayer.removeAllListeners === 'function') {
          existingPlayer.removeAllListeners();
        }
      } catch (err) {
        console.error('Error stopping existing MIDI player:', err);
      }
      stopAllNotes();
      setMidiPlayer(null);
      midiPlayerRef.current = null;

      // Wait a bit for cleanup
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Load MidiPlayer dynamically
    if (typeof window.MidiPlayer === 'undefined') {
      try {
        const module = await import(/* webpackIgnore: true */ 'https://cdn.jsdelivr.net/npm/midi-player-js@2.0.16/build/index.browser.min.js');
        window.MidiPlayer = module.default || module.MidiPlayer || module;
      } catch (err) {
        console.error('Error loading MidiPlayer:', err);
        return;
      }
    }

    // Create a wrapper that checks isPlayingRef before processing events
    const eventHandler = (event) => {
      // Check the ref for current playing state (refs always have current value)
      if (!isPlayingRef.current) {
        return; // Don't process events if not playing
      }
      onMidiEvent(event);
    };

    const player = new window.MidiPlayer.Player(eventHandler);
    player.on('endOfFile', onMidiEnd);
    player.loadArrayBuffer(arrayBuffer);

    // Set both state and ref
    setMidiPlayer(player);
    midiPlayerRef.current = player;
  };

  const onMidiEvent = (event) => {
    // Double check isPlaying state using ref
    if (!isPlayingRef.current) {
      return;
    }

    const ctx = audioContextRef.current || audioContext;
    if (!ctx) return;

    const noteKey = event.noteNumber;

    if (event.name === 'Note on' && event.velocity > 0) {
      const isSimple = useSimpleSynth || !piano;

      if (isSimple) {
        const node = createSimpleNote(event.noteName, event.velocity / 127);
        setActiveAudioNodes(prev => ({ ...prev, [noteKey]: node }));
      } else if (piano && typeof piano.play === 'function') {
        try {
          const node = piano.play(event.noteName, ctx.currentTime, {
            gain: event.velocity / 127
          });
          setActiveAudioNodes(prev => ({ ...prev, [noteKey]: node }));
        } catch (err) {
          console.error('Error playing note:', err);
          const node = createSimpleNote(event.noteName, event.velocity / 127);
          setActiveAudioNodes(prev => ({ ...prev, [noteKey]: node }));
        }
      } else {
        const node = createSimpleNote(event.noteName, event.velocity / 127);
        setActiveAudioNodes(prev => ({ ...prev, [noteKey]: node }));
      }
    } else if (event.name === 'Note off' || (event.name === 'Note on' && event.velocity === 0)) {
      if (activeAudioNodes[noteKey]) {
        if (activeAudioNodes[noteKey].stop) {
          const ctx = audioContextRef.current || audioContext;
          if (useSimpleSynth) {
            activeAudioNodes[noteKey].stop();
          } else if (ctx) {
            activeAudioNodes[noteKey].stop(ctx.currentTime);
          } else {
            activeAudioNodes[noteKey].stop();
          }
        }
        setActiveAudioNodes(prev => {
          const newNodes = { ...prev };
          delete newNodes[noteKey];
          return newNodes;
        });
      }
    }
  };

  const createSimpleNote = (noteName, velocity) => {
    const ctx = audioContextRef.current || audioContext;
    if (!ctx) return null;

    const frequency = noteNameToFrequency(noteName);

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;

    const currentTime = ctx.currentTime;
    gainNode.gain.setValueAtTime(0, currentTime);
    gainNode.gain.linearRampToValueAtTime(velocity * 0.3, currentTime + 0.01);

    const releaseTime = 0.5;

    oscillator.start(currentTime);

    return {
      oscillator: oscillator,
      gain: gainNode,
      stop: function () {
        const stopTime = ctx.currentTime;
        gainNode.gain.linearRampToValueAtTime(0, stopTime + releaseTime);
        setTimeout(() => {
          try {
            oscillator.stop();
          } catch (e) {
            // Oscillator already stopped
          }
        }, releaseTime * 1000);
      }
    };
  };

  const noteNameToFrequency = (noteName) => {
    const A4 = 440;
    const noteMap = {
      'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3,
      'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8,
      'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11
    };

    const match = noteName.match(/^([A-G][#b]?)(\d+)$/);
    if (!match) return A4;

    const note = match[1];
    const octave = parseInt(match[2]);
    const semitone = noteMap[note] || 0;
    const semitonesFromA4 = (octave - 4) * 12 + semitone - 9;

    return A4 * Math.pow(2, semitonesFromA4 / 12);
  };

  const stopAllNotes = () => {
    const ctx = audioContextRef.current || audioContext;

    // Stop all active audio nodes
    Object.values(activeAudioNodes).forEach(node => {
      try {
        if (node) {
          // Handle custom note objects (with oscillator and gain properties)
          if (node.oscillator && node.gain) {
            try {
              // Stop the oscillator immediately
              if (node.oscillator && typeof node.oscillator.stop === 'function') {
                node.oscillator.stop();
              }
              // Disconnect gain node
              if (node.gain && typeof node.gain.disconnect === 'function') {
                node.gain.disconnect();
              }
              // Disconnect oscillator
              if (node.oscillator && typeof node.oscillator.disconnect === 'function') {
                node.oscillator.disconnect();
              }
            } catch (e) {
              // Node might already be stopped, that's okay
            }
          }

          // Handle custom note objects with stop method
          if (node.stop && typeof node.stop === 'function') {
            try {
              node.stop();
            } catch (e) {
              // Node might already be stopped, that's okay
            }
          }
          // Try to stop oscillators directly
          else if (typeof node.stop === 'function') {
            try {
              node.stop();
            } catch (e) {
              // Node might already be stopped, that's okay
            }
          }

          // Try to disconnect audio nodes
          if (typeof node.disconnect === 'function') {
            try {
              node.disconnect();
            } catch (e) {
              // Node might already be disconnected, that's okay
            }
          }
        }
      } catch (err) {
        console.error('Error stopping audio node:', err);
      }
    });

    // Clear all active nodes immediately
    setActiveAudioNodes({});

    // Also try to stop any ongoing audio context operations
    if (ctx && ctx.state !== 'closed') {
      try {
        // Suspend the context to stop all audio immediately
        if (ctx.state === 'running') {
          ctx.suspend().then(() => {
            // Resume it so it's ready for next play
            ctx.resume();
          }).catch(err => {
            console.error('Error suspending audio context:', err);
          });
        }
      } catch (err) {
        console.error('Error managing audio context:', err);
      }
    }
  };

  const onMidiEnd = () => {
    stopAllNotes();
    isPlayingRef.current = false;
    setIsPlaying(false);
  };

  const handleCopy = () => {
    if (abcTextareaRef.current) {
      abcTextareaRef.current.select();
      document.execCommand('copy');
      // showSuccess('Copied to clipboard!');
    }
  };

  const handleDownload = async (format) => {
    if (!isAuthenticated) {
      showError('Please register or log in to use this feature.');
      return;
    }
    const fileName = asset?.original_filename || asset?.filename || 'tune';
    const baseName = fileName.replace(/\.(abc|txt)$/i, '');

    if (format === 'abc') {
      const blob = new Blob([abcContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseName}.abc`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'pdf') {
      if (!currentVisualObj || currentVisualObj.length === 0) {
        showError('No notation to download. Please wait for the notation to render.');
        return;
      }

      try {
        // Load jsPDF if not already loaded
        if (typeof window.jsPDF === 'undefined' && typeof window.jspdf === 'undefined') {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = '/tools/abctools/jspdf.umd.min.js';
            script.onload = () => {
              // Wait a bit for the module to initialize
              setTimeout(resolve, 100);
            };
            script.onerror = reject;
            document.body.appendChild(script);
          });
        }

        // Try different ways jsPDF might be exposed
        let jsPDFClass;
        if (window.jsPDF && window.jsPDF.jsPDF) {
          jsPDFClass = window.jsPDF.jsPDF;
        } else if (window.jsPDF) {
          jsPDFClass = window.jsPDF;
        } else if (window.jspdf && window.jspdf.jsPDF) {
          jsPDFClass = window.jspdf.jsPDF;
        } else {
          throw new Error('jsPDF library not loaded correctly');
        }

        const pdf = new jsPDFClass({ orientation: 'landscape', unit: 'mm' });
        const svg = scoreDivRef.current?.querySelector('svg');

        if (svg) {
          // Get SVG dimensions - try multiple methods
          let svgWidth, svgHeight;

          // First try getBoundingClientRect (actual rendered size)
          const svgRect = svg.getBoundingClientRect();
          if (svgRect.width > 0 && svgRect.height > 0) {
            svgWidth = svgRect.width;
            svgHeight = svgRect.height;
          } else {
            // Try width/height attributes
            if (svg.width && svg.width.baseVal) {
              svgWidth = svg.width.baseVal.value;
            }
            if (svg.height && svg.height.baseVal) {
              svgHeight = svg.height.baseVal.value;
            }

            // If still no dimensions, try viewBox
            if ((!svgWidth || !svgHeight) && svg.viewBox && svg.viewBox.baseVal) {
              svgWidth = svg.viewBox.baseVal.width || 800;
              svgHeight = svg.viewBox.baseVal.height || 600;
            }

            // Fallback to default
            svgWidth = svgWidth || 800;
            svgHeight = svgHeight || 600;
          }

          // Scale factor for high resolution (3x for crisp output)
          const scale = 3;
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Set canvas size with scale factor
          canvas.width = svgWidth * scale;
          canvas.height = svgHeight * scale;

          const img = new Image();

          // Serialize SVG with proper encoding
          const svgData = new XMLSerializer().serializeToString(svg);
          const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
          const url = URL.createObjectURL(svgBlob);

          img.onload = function () {
            try {
              // Draw the SVG image on canvas at full scale
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

              // Get high-resolution image data
              const imgData = canvas.toDataURL('image/png', 1.0);

              // Calculate PDF dimensions (A4 landscape: 297mm x 210mm)
              const pdfWidth = 277; // 297 - 20 (margins)
              const pdfHeight = 190; // 210 - 20 (margins)

              // Calculate aspect ratio to fit
              const aspectRatio = svgHeight / svgWidth;
              let imgWidth = pdfWidth;
              let imgHeight = imgWidth * aspectRatio;

              // If height exceeds, scale down
              if (imgHeight > pdfHeight) {
                imgHeight = pdfHeight;
                imgWidth = imgHeight / aspectRatio;
              }

              // Center the image
              const x = (297 - imgWidth) / 2;
              const y = (210 - imgHeight) / 2;

              pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
              pdf.save(`${baseName}.pdf`);
            } catch (err) {
              console.error('Error creating PDF:', err);
              showError('Failed to create PDF. ' + err.message);
            } finally {
              // Clean up blob URL
              URL.revokeObjectURL(url);
            }
          };

          img.onerror = function () {
            showError('Failed to convert notation to image for PDF.');
            URL.revokeObjectURL(url);
          };

          img.src = url;
        } else {
          showError('No notation found to export as PDF.');
        }
      } catch (err) {
        console.error('Error downloading PDF:', err);
        showError('Failed to download PDF. ' + err.message);
      }
    } else if (format === 'png') {
      if (!currentVisualObj || currentVisualObj.length === 0) {
        showError('No notation to download. Please wait for the notation to render.');
        return;
      }

      try {
        // Load html2canvas if not already loaded
        if (typeof window.html2canvas === 'undefined') {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = '/tools/abctools/html2canvas.min.js';
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
          });
        }

        const svg = scoreDivRef.current?.querySelector('svg');

        if (svg) {
          const canvas = await window.html2canvas(svg.parentElement, {
            backgroundColor: '#ffffff'
          });
          canvas.toBlob(function (blob) {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${baseName}.png`;
            a.click();
            URL.revokeObjectURL(url);
          });
        } else {
          showError('No notation found to export as image.');
        }
      } catch (err) {
        console.error('Error downloading PNG:', err);
        showError('Failed to download image. ' + err.message);
      }
    } else if (format === 'midi') {
      if (!currentVisualObj || currentVisualObj.length === 0) {
        showError('No notation to download. Please wait for the notation to render.');
        return;
      }

      try {
        const midiData = window.ABCJS.synth.getMidiFile(currentVisualObj[0], {
          qpm: currentBPM,
          midiTempoAdjustment: 0
        });

        if (!midiData) {
          showError('Could not generate MIDI data.');
          return;
        }

        let arrayBuffer;
        if (midiData instanceof Uint8Array) {
          arrayBuffer = midiData.buffer.slice(midiData.byteOffset, midiData.byteOffset + midiData.byteLength);
        } else if (midiData instanceof ArrayBuffer) {
          arrayBuffer = midiData;
        } else if (typeof midiData === 'string') {
          // Handle data URL string
          const dataUrlMatch = midiData.match(/href="(data:audio\/midi[^"]+)"/);
          let dataUrl = dataUrlMatch && dataUrlMatch[1];
          if (!dataUrl) {
            const altMatch = midiData.match(/data:audio\/midi,([^"]+)/);
            if (altMatch && altMatch[1]) {
              dataUrl = 'data:audio/midi,' + altMatch[1];
            }
          }
          if (dataUrl) {
            const response = await fetch(dataUrl);
            arrayBuffer = await response.arrayBuffer();
          }
        }

        if (arrayBuffer && arrayBuffer.byteLength > 0) {
          const blob = new Blob([arrayBuffer], { type: 'audio/midi' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${baseName}.mid`;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          showError('Could not extract MIDI data.');
        }
      } catch (err) {
        console.error('Error downloading MIDI:', err);
        showError('Failed to download MIDI. ' + err.message);
      }
    }
  };

  // Parse ABC metadata from description
  const parseABCMetadata = () => {
    if (!asset?.description) return {};

    const abcMatch = asset.description.match(/\[ABC_METADATA\](.*?)\[\/ABC_METADATA\]/s);
    if (abcMatch) {
      try {
        return JSON.parse(abcMatch[1]);
      } catch (e) {
        return {};
      }
    }
    return {};
  };

  const loadRelatedContent = async () => {
    if (!asset || !asset.id) {
      console.log('[ABCAssetViewer] No asset or asset.id, skipping related content load');
      return;
    }

    console.log(`[ABCAssetViewer] Loading related content for asset ID: ${asset.id}`);
    setLoadingRelated(true);

    try {
      // Try to fetch related products from asset-specific endpoint first
      console.log(`[ABCAssetViewer] Fetching related products from /api/assets/${asset.id}/products`);
      const productsResponse = await fetch(`/api/assets/${asset.id}/products`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (productsResponse.ok) {
        const products = await productsResponse.json();
        console.log(`[ABCAssetViewer] Loaded ${products?.length || 0} related products from asset endpoint`);
        setRelatedProducts(products || []);
      } else {
        // Fallback to content endpoint
        console.log(`[ABCAssetViewer] Asset products endpoint returned ${productsResponse.status}, trying content endpoint`);
        const fallbackResponse = await fetch(`/api/content/tunes/${asset.id}/products`);
        if (fallbackResponse.ok) {
          const products = await fallbackResponse.json();
          console.log(`[ABCAssetViewer] Loaded ${products?.length || 0} related products from content endpoint`);
          setRelatedProducts(products || []);
        } else {
          console.log(`[ABCAssetViewer] Content products endpoint also returned ${fallbackResponse.status}`);
          setRelatedProducts([]);
        }
      }
    } catch (error) {
      console.error('[ABCAssetViewer] Error loading related products:', error);
      setRelatedProducts([]);
    }

    try {
      // Try to fetch related tunes - use asset-specific endpoint for ABC assets
      console.log(`[ABCAssetViewer] Fetching related tunes from /api/content/assets/${asset.id}/related`);
      const tunesResponse = await fetch(`/api/content/assets/${asset.id}/related`);

      if (tunesResponse.ok) {
        const tunes = await tunesResponse.json();
        console.log(`[ABCAssetViewer] Loaded ${tunes?.length || 0} related tunes:`, tunes);
        setRelatedTunes(tunes || []);
      } else {
        const errorText = await tunesResponse.text();
        console.error(`[ABCAssetViewer] Related tunes endpoint returned ${tunesResponse.status}:`, errorText);
        // Fallback to page-based endpoint if asset endpoint fails
        console.log(`[ABCAssetViewer] Trying fallback endpoint /api/content/tunes/related/${asset.id}`);
        const fallbackResponse = await fetch(`/api/content/tunes/related/${asset.id}`);
        if (fallbackResponse.ok) {
          const tunes = await fallbackResponse.json();
          console.log(`[ABCAssetViewer] Fallback loaded ${tunes?.length || 0} related tunes`);
          setRelatedTunes(tunes || []);
        } else {
          console.log(`[ABCAssetViewer] Fallback also failed with ${fallbackResponse.status}`);
          setRelatedTunes([]);
        }
      }
    } catch (error) {
      console.error('[ABCAssetViewer] Error loading related tunes:', error);
      setRelatedTunes([]);
    } finally {
      setLoadingRelated(false);
    }
  };

  const metadata = parseABCMetadata();
  const tuneTitle = metadata.tune_title || asset?.original_filename || 'ABC Tune';

  // Get description text (without metadata tags)
  const getDescriptionText = () => {
    if (!asset?.description) return '';
    return asset.description.replace(/\[ABC_METADATA\].*?\[\/ABC_METADATA\]/s, '').trim();
  };

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const descriptionText = getDescriptionText();
  const showFeaturedIn = loadingRelated || relatedProducts.length > 0;
  const showRelatedTunes = loadingRelated || relatedTunes.length > 0;

  const sectionLinks = [
    { id: 'standard-notation', label: 'Standard Notation', title: 'Standard Notation' },
    { id: 'about-tune', label: 'About This Tune', title: 'About This Tune' },
    { id: 'related-tunes', label: 'Related Tunes', title: 'Related Tunes' },
  ];

  if (loading || convertingMidi) {
    return (
      <div className="abc-asset-viewer">
        <LoadingSpinner
          message={convertingMidi ? 'Converting MIDI to ABC notation...' : 'Loading ABC tune...'}
          size="large"
        />
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="abc-asset-viewer">
        <div className="error-message">
          <h2>Asset not found</h2>
          <button onClick={() => navigate('/tunes')}>Back to Tunes</button>
        </div>
      </div>
    );
  }

  return (
    <div className="abc-asset-viewer">
      <SEO
        title={`${tuneTitle} | Cranford Publications`}
        description={descriptionText || `Interactive ABC notation for ${tuneTitle}.`}
        canonical={`/tune-asset/${asset.id}`}
        type="music.song"
      />
      {showEditModal && (
        <AssetEditModal 
          asset={asset} 
          onClose={() => setShowEditModal(false)} 
          onSave={() => {
            setShowEditModal(false);
            loadAsset(); // Refresh the viewer with updated Title and DB Metadata
          }} 
        />
      )}
      {/* Tune Header — share + edit live in the header row (fixed floating looked wrong on this layout) */}
      <div className="tune-header">
        <div className="tune-header-inner">
          <div className="tune-header-main">
            <h1 className="tune-title">{tuneTitle}</h1>
            {metadata.rhythm_style && (
              <p className="tune-subtitle">{metadata.rhythm_style} • {metadata.key_signature || metadata.key || 'Key: N/A'}</p>
            )}
          </div>
          <div className="tune-header-actions">
            <button
              type="button"
              className="share-btn-abc"
              onClick={() => triggerShare({
                title: tuneTitle,
                url: `${window.location.origin}/tune-asset/${asset.id}`,
                text: tuneTitle,
                openEmailModal: () => setShowShareModal(true),
                showSuccess,
              })}
              title="Share this ABC tune"
              aria-label="Share this tune"
            >
              <img src="/icons/share-icon.svg" alt="" />
            </button>
            {isAuthenticated && currentUser?.roles?.some(r => ['admin', 'super_admin', 'content_creator'].includes(r)) && (
              <button
                type="button"
                className="tune-header-edit-btn"
                onClick={() => setShowEditModal(true)}
              >
                ✏️ Edit Asset
              </button>
            )}
          </div>
        </div>
      </div>

      {sectionLinks.length > 0 && (
        <div className="abc-section-jump-wrap">
          <nav className="abc-section-jump" aria-label="Jump to sections">
            <span className="abc-section-jump-label">Jump to :</span>
            <div className="abc-section-jump-buttons">
              {sectionLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  className="abc-section-jump-btn"
                  onClick={() => scrollToSection(link.id)}
                  title={link.title}
                >
                  {link.label}
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}

      {/* ABC and Controls Container */}
      <div className="abc-controls-container">
        {/* ABC Code Section */}
        <div className="abc-code-section" id="abc-notation">
          <h3>ABC Notation</h3>
          <textarea
            ref={abcTextareaRef}
            id="abc"
            value={abcContent}
            onChange={(e) => {
              setAbcContent(e.target.value);
              renderNotation(e.target.value);
            }}
            readOnly={false}
          />
          <button
            id="updateNotationBtn"
            onClick={() => renderNotation(abcContent)}
            style={{ marginTop: '10px', width: '100%' }}
          >
            🔄 Update Notation from ABC Text
          </button>
        </div>

        {/* Controls Panel */}
        <div className="controls-panel" id="abc-controls">
          <h3>Controls</h3>

          {/* BPM Control */}
          <div className="bpm-section">
            <label>
              Tempo (BPM): <span id="bpm-value">{currentBPM}</span>
            </label>
            <input
              type="range"
              id="bpm-slider"
              min="60"
              max="200"
              value={currentBPM}
              onChange={(e) => setCurrentBPM(parseInt(e.target.value))}
            />
          </div>

          {/* Instrument Selection */}
          <div className="instrument-section">
            <label htmlFor="instrument-select">Instrument:</label>
            <select
              id="instrument-select"
              value={currentInstrument}
              onChange={(e) => {
                setCurrentInstrument(e.target.value);
                loadSoundfont(e.target.value);
              }}
            >
              <option value="piano-soundfont-fluidr3">Piano - Standard (FluidR3_GM)</option>
              <option value="piano-soundfont-musyngkite">Piano - HQ Grand Piano (MusyngKite)</option>
              <option value="piano-simple">Piano - Simple Synth</option>
            </select>
          </div>

          <div className="controls-section">
            <button
              id="play-button"
              onClick={handlePlay}
              disabled={initialLoadDelay || loading || isLoadingAudio || isPlaying || !currentVisualObj || currentVisualObj.length === 0}
              title={initialLoadDelay ? 'Initializing...' : loading ? 'Loading tune...' : isLoadingAudio ? 'Loading audio system...' : !currentVisualObj || currentVisualObj.length === 0 ? 'No notation available' : isPlaying ? 'Currently playing - click Stop first' : 'Play the tune'}
            >
              {initialLoadDelay ? '⏳ Play (...loading)' : loading ? '⏳ Play (...loading)' : isLoadingAudio ? '⏳ Play (...loading)' : isPlaying ? '▶ Playing...' : '▶ Play'}
            </button>
            <button
              id="stop-button"
              onClick={handleStop}
              disabled={!isPlaying}
            >
              ⏹ Stop
            </button>
            <button id="copyBtn" onClick={handleCopy}>📋 Copy ABC</button>
            <button id="saveBtn" onClick={() => handleDownload('abc')} disabled={!isAuthenticated} title={!isAuthenticated ? 'Please log in or register to use this feature.' : ''}>💾 Save ABC</button>
            <button id="downloadPdfBtn" onClick={() => handleDownload('pdf')} disabled={!isAuthenticated} title={!isAuthenticated ? 'Please log in or register to use this feature.' : ''}>📄 Download PDF</button>
            <button id="downloadPngBtn" onClick={() => handleDownload('png')} disabled={!isAuthenticated} title={!isAuthenticated ? 'Please log in or register to use this feature.' : ''}>🖼️ Download Image</button>
            <button id="downloadMidiBtn" onClick={() => handleDownload('midi')} disabled={!isAuthenticated} title={!isAuthenticated ? 'Please log in or register to use this feature.' : ''}>🎵 Download MIDI</button>
            {!isAuthenticated && (
              <p style={{ width: '100%', textAlign: 'center', marginTop: '10px', color: '#666', fontStyle: 'italic', fontSize: '0.9em' }}>
                Please{' '}
                <a
                  href="https://www.cranfordpub.ca/login"
                  style={{ color: '#1a5f9e', textDecoration: 'underline', fontStyle: 'normal' }}
                >
                  log in
                </a>
                {' '}or register to use grayed out features.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Notation Section */}
      <div className="notation-section" id="standard-notation">
        <h2>Standard Notation</h2>
        <div ref={scoreDivRef} id="score" style={{ background: 'white' }}>
          Notation will appear here
        </div>
      </div>

      {/* About This Tune */}
      {descriptionText && (
        <div className="tune-description" id="about-tune">
          <h2 className="tune-section-header">About This Tune</h2>
          <div className="tune-description-text">
            {descriptionText.split('\n').map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </div>
      )}

      {/* Related Products/Books */}
      {showFeaturedIn && (
      <div className="related-products-section" id="featured-in">
        <div className="section-header">
          <h3>📚 Featured In</h3>
        </div>
        <div className="related-products-grid">
          {loadingRelated ? (
            <div className="related-placeholder">
              <div className="related-placeholder-icon">📖</div>
              <p>Loading related books...</p>
            </div>
          ) : (
            relatedProducts.map((product) => (
              <div key={product.id} className="related-product-card">
                <a href={`/product/${product.id}`} className="related-product-link">
                  {product.image && (
                    <div className="related-product-image">
                      <img
                        src={product.image}
                        alt={displayRelatedText(product.name)}
                        className="product-image"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div className="related-product-info">
                    <h4 className="related-product-name">{displayRelatedText(product.name)}</h4>
                    {product.author && (
                      <p className="related-product-author">
                        by {displayRelatedText(product.author)}
                      </p>
                    )}
                    <div className="related-product-footer">
                      <span className="related-product-price">
                        ${product.price ? product.price.toFixed(2) : (product.priceCents / 100).toFixed(2)}
                      </span>
                      {product.tags && (
                        <div className="related-product-tags">
                          {product.tags.split(',').slice(0, 2).map((tag, idx) => (
                            <span key={idx} className="product-tag">
                              {tag.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </a>
              </div>
            ))
          )}
        </div>
      </div>
      )}

      {/* Related Tunes */}
      {showRelatedTunes && (
      <div className="related-tunes" id="related-tunes">
        <h2 className="tune-section-header">🎵 Related Tunes</h2>
        <div className="related-content-grid">
          {loadingRelated ? (
            <div className="related-placeholder">
              <div className="related-placeholder-icon">🎼</div>
              <p>Loading related tunes...</p>
            </div>
          ) : (
            relatedTunes.map((tune) => (
              <a
                key={tune.page_id || tune.asset_id}
                href={tune.asset_id ? `/tune-asset/${tune.asset_id}` : `/page/${tune.page_id}`}
                className="related-tune-card related-tune-card--marked"
              >
                <div className="tune-card-header">
                  <h3 className="tune-card-title">{displayRelatedText(tune.title)}</h3>
                  <TuneFormatMarker
                    item={{
                      ...tune,
                      type: tune.asset_id ? 'abc-asset' : 'tune-notation',
                      assetId: tune.asset_id,
                    }}
                    variant="inline"
                  />
                  {tune.score && (
                    <span className="tune-card-score">{tune.score.toFixed(1)}</span>
                  )}
                </div>
                <p className="tune-card-meta">
                  {displayRelatedText(tune.region || 'Traditional')} • {displayRelatedText(tune.tune_type || 'Tune')}
                </p>
                {tune.artist && (
                  <p className="tune-card-artist">🎻 {displayRelatedText(tune.artist)}</p>
                )}
                {tune.album && (
                  <p className="tune-card-album">💿 {displayRelatedText(tune.album)}</p>
                )}
                {tune.tags && (
                  <div className="tune-card-tags">
                    {(typeof tune.tags === 'string'
                      ? tune.tags.split(',').map(t => t.trim()).filter(t => t)
                      : Array.isArray(tune.tags) ? tune.tags : []
                    ).slice(0, 4).map((tag, i) => (
                      <span key={i} className="tune-card-tag">
                        {displayRelatedText(String(tag).trim())}
                      </span>
                    ))}
                  </div>
                )}
              </a>
            ))
          )}
        </div>
      </div>
      )}

      {asset && asset.id ? (
        <UserContentFeedback
          targetType="abc_asset"
          targetId={String(asset.id)}
          settingKey="user_ratings_abc_tune_enabled"
          title="Rate this tune"
        />
      ) : null}

      {asset && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          contentTitle={tuneTitle}
          contentUrl={`${window.location.origin}/tune-asset/${asset.id}`}
          contentId={String(asset.id)}
          contentType="tune-asset"
          contentDescription={getDescriptionText() || ''}
        />
      )}
    </div>
  );
};

export default ABCAssetViewer;

